import { useLayoutEffect, useRef, useState, type ReactNode, type Ref } from 'react';
import { Sheet, SheetContent } from '@/components/ui-kit/sheet';
import { useMediaQuery } from '@/hooks/use-media-query';

interface ReviewShellProps {
  /** Sticky page header (title, status, primary actions). */
  header: ReactNode;
  /** Optional strip under the header, e.g. the guest paywall banner. */
  banner?: ReactNode;

  /** Zoom / regenerate / sidebar-toggle bar above the document. */
  toolbar: ReactNode;
  /** The document surface — a PDF preview or a rendered .docx. */
  viewer: ReactNode;
  /** Attached to the scrolling viewport so callers can measure and scroll it. */
  scrollContainerRef: Ref<HTMLDivElement>;

  /** Tabbed sidebar body, shared by the desktop aside and the mobile sheet. */
  sidebar: ReactNode;
  /**
   * Pinned sidebar actions. Omit to hide — the caller drops it while the block
   * editor is open, since the editor has its own Save/Cancel and the extra bar
   * crowds short laptop screens.
   */
  sidebarFooter?: ReactNode;
  sidebarOpen: boolean;
  onSidebarOpenChange: (open: boolean) => void;

  /** Fixed bottom action bar, mobile only. */
  mobileActionBar?: ReactNode;
  /** Dialogs to mount at the page root. */
  dialogs?: ReactNode;
  /** Full-screen overlay, e.g. while preparing a contract. */
  overlay?: ReactNode;
}

/**
 * Layout chrome for the document review page: header, toolbar, scrolling
 * document viewport, and the sidebar in both its desktop (persistent aside) and
 * mobile (slide-in sheet) forms.
 *
 * Deliberately knows nothing about the document model — every region is a slot.
 * That's what lets the `LegalDocument` review and the fixed-DOCX review share
 * one page instead of maintaining two near-identical shells.
 */
export function ReviewShell({
  header,
  banner,
  toolbar,
  viewer,
  scrollContainerRef,
  sidebar,
  sidebarFooter,
  sidebarOpen,
  onSidebarOpenChange,
  mobileActionBar,
  dialogs,
  overlay,
}: ReviewShellProps) {
  // Mobile = below Tailwind's `md` breakpoint (≥768px). The mobile Sheet is
  // mounted only here so its body-portaled overlay can never cover desktop —
  // gating the mount also stops opening the sidebar programmatically (e.g. when
  // opening the editor) from painting a full-screen overlay on desktop.
  const isMobile = useMediaQuery('(max-width: 767px)');

  // This page renders inside the global MainLayout (navbar + min-h-screen), so
  // it can't assume it owns the viewport. Measure the shell's distance from the
  // top and give it exactly the remaining height — recomputed on scroll/resize
  // — so the sidebar footer stays pinned on-screen both before and after the
  // navbar scrolls away. useLayoutEffect avoids a first-paint flash.
  const shellRef = useRef<HTMLDivElement>(null);
  const [shellHeight, setShellHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (isMobile) {
      setShellHeight(null);
      return;
    }
    const measure = () => {
      const el = shellRef.current;
      if (!el) return;
      setShellHeight(Math.max(0, window.innerHeight - el.getBoundingClientRect().top));
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, { passive: true });
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure);
    };
  }, [isMobile]);

  return (
    <div
      ref={shellRef}
      style={isMobile || shellHeight == null ? undefined : { height: shellHeight }}
      className="min-h-screen md:min-h-0 md:overflow-hidden flex flex-col bg-linear-to-b from-[hsl(var(--gradient-light-from))] to-[hsl(var(--gradient-light-to))] text-[hsl(var(--fg))]"
    >
      {header}
      {banner}

      <div className="flex-1 md:min-h-0 flex flex-col md:flex-row ">
        <div className="flex-1 min-w-0 md:min-h-0 flex flex-col  bg-[hsl(var(--bg))] relative">
          {toolbar}
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-auto  p-2 md:p-4 sm:p-6 bg-muted/40 rounded-b-2xl pb-24 md:pb-6"
          >
            {viewer}
          </div>
        </div>

        {/* Desktop sidebar — persistent two-column from md upwards. The shell is
            a bounded flex container on desktop, so the aside just fills its row
            (md:h-full): the tabs scroll internally and the footer pins to the
            bottom, with no vh math that could spill past the viewport. */}
        <aside className="hidden md:flex md:flex-col md:w-80 lg:w-100   bg-[hsl(var(--bg))]  border-l  overflow-hidden md:h-full">
          {sidebar}
          {sidebarFooter && <div className="shrink-0 rounded-b-2xl pb-2">{sidebarFooter}</div>}
        </aside>

        {isMobile && (
          <Sheet open={sidebarOpen} onOpenChange={onSidebarOpenChange}>
            <SheetContent side="right" className="w-[85vw] sm:max-w-sm p-0 flex flex-col gap-0">
              {sidebar}
              {sidebarFooter}
            </SheetContent>
          </Sheet>
        )}
      </div>

      {mobileActionBar}
      {dialogs}
      {overlay}
    </div>
  );
}
